/** Studio commerce text in English (the source language). Short, plain sentences. */
export const commerceEn = {
  nav: { orders: 'Orders', finance: 'Finance exceptions' },
  orders: {
    title: 'Orders',
    description:
      'Orders placed in chat. Payment and refund states come from the payment provider, never from the chat.',
    search: 'Search by order reference',
    needsAttention: 'Only orders that need attention',
    empty: 'No orders yet.',
    emptyFiltered: 'No orders match.',
    clearFilters: 'Show all orders',
    loading: 'Loading orders',
    columns: {
      reference: 'Order',
      customer: 'Customer',
      placed: 'Placed',
      total: 'Total',
      payment: 'Payment',
      fulfilment: 'Fulfilment',
      attention: 'Needs attention',
    },
    visitor: 'Visitor',
    pendingRefunds_one: '{{count}} refund waiting',
    pendingRefunds_other: '{{count}} refunds waiting',
    openExceptions_one: '{{count}} exception',
    openExceptions_other: '{{count}} exceptions',
  },
  payment: {
    not_started: 'Not paid',
    requires_action: 'Needs the customer',
    processing: 'Confirming',
    authorized: 'Authorized',
    captured: 'Paid',
    failed: 'Failed',
    cancelled: 'Cancelled',
    unknown: 'Not confirmed',
  },
  orderState: {
    draft: 'Draft',
    submitted: 'Waiting for payment',
    confirmed: 'Confirmed',
    partially_fulfilled: 'Partly fulfilled',
    fulfilled: 'Fulfilled',
    cancel_requested: 'Cancellation asked',
    cancelled: 'Cancelled',
    returned: 'Returned',
  },
  fulfilment: {
    not_started: 'Not started',
    pending: 'Pending',
    in_progress: 'In progress',
    delayed: 'Delayed',
    shipped: 'Shipped',
    delivered: 'Delivered',
    provisioned: 'Active',
    failed: 'Failed',
    cancelled: 'Cancelled',
  },
  detail: {
    loading: 'Loading order',
    back: 'All orders',
    title: 'Order {{reference}}',
    observed: 'Read from the records at {{time}}.',
    seller: 'Seller',
    customer: 'Customer',
    items: 'Items',
    delivery: 'Delivery',
    total: 'Total',
    payment: 'Payment',
    paidAt: 'Paid at',
    providerRef: 'Provider reference',
    paid: 'Paid',
    refunded: 'Refunded',
    refundable: 'Can still be refunded',
    fulfilments: 'Fulfilment',
    noFulfilment: 'No fulfilment updates yet.',
    estimate: 'Estimate from the shop: {{date}}',
    kind: { shipment: 'Shipment', service: 'Service' },
    source: { merchant: 'From the shop system', staff: 'Recorded by the team' },
    tracking: 'Tracking reference: {{ref}}',
    updated: 'Updated {{time}}',
    operations: 'Provider calls',
    operationsDescription:
      'Every call to the payment provider, with what was observed, from where and when.',
    operationState: {
      pending: 'Not sent',
      dispatched: 'Sent',
      succeeded: 'Done',
      failed: 'Failed',
      unknown: 'Outcome not known yet',
      cancelled: 'Cancelled',
    },
    receipt: '{{kind}} · {{outcome}} · {{source}} · {{time}}',
    exceptions: 'Exceptions',
  },
  fulfilmentForm: {
    open: 'Record an update',
    title: 'Record a fulfilment update',
    description: 'Record what the shop reported. The customer sees the new status in the chat.',
    kind: 'What is being fulfilled',
    state: 'New status',
    estimate: 'Estimated date',
    estimateHint: 'Only a date the shop gave. The customer sees it as an estimate, not a promise.',
    carrierRef: 'Tracking reference',
    note: 'Note for the team',
    noteHint: 'The customer does not see this note.',
    submit: 'Save and tell the customer',
    saved: 'Update saved. The customer sees it in the chat.',
  },
  refunds: {
    title: 'Refunds',
    none: 'No refunds yet.',
    request: 'Request a refund',
    requestDescription: 'Up to {{amount}} can be refunded.',
    policyAlways: 'Every refund needs approval by a second person.',
    policyThreshold: 'Refunds above {{amount}} need approval by a second person.',
    amount: 'Amount in {{currency}}',
    fullOnly: 'This shop can only refund the full amount: {{amount}}.',
    amountHint: 'For example 35.00',
    ambiguous: 'Which amount do you mean?',
    invalid: {
      empty: 'Enter an amount.',
      format: 'Enter an amount like 35.00.',
      precision: 'Too many decimal places for {{currency}}.',
      negative: 'Enter an amount above zero.',
      too_large: 'This amount is too large.',
      over: 'This is more than can be refunded.',
    },
    reason: 'Reason',
    reasons: {
      customer_request: 'Customer asked',
      damaged: 'Damaged',
      not_delivered: 'Not delivered',
      duplicate: 'Charged twice',
      other: 'Other',
    },
    note: 'Note for the team',
    submit: 'Request refund',
    state: {
      requested: 'Requested',
      policy_checked: 'Checked',
      awaiting_approval: 'Waiting for approval',
      submitted: 'Sent to the provider',
      pending: 'Pending with the provider',
      succeeded: 'Refunded',
      rejected: 'Rejected',
      failed: 'Failed',
      unknown: 'Outcome not confirmed',
    },
    requestedBy: 'Asked by {{name}}, {{time}}',
    approvedBy: 'Approved by {{name}}, {{time}}',
    rejectedBy: 'Rejected by {{name}}: {{reason}}',
    failureReason: 'The provider said: {{reason}}',
    approve: 'Approve refund',
    reject: 'Reject',
    check: 'Check refund status',
    approveTitle: 'Approve a refund of {{amount}}?',
    approveBody:
      'The money goes back to the customer’s payment method through the payment provider. This cannot be undone here.',
    approveConfirm: 'Approve and send',
    rejectTitle: 'Reject this refund?',
    rejectReason: 'Why? (the team sees this)',
    rejectConfirm: 'Reject refund',
    samePerson: 'You asked for this refund, so someone else must approve it.',
    noPermission: 'Only people who can approve refunds can decide this one.',
    separateApprover: 'Someone other than the person who asked must approve this refund.',
    pendingNote: 'It is not refunded until the provider confirms it.',
  },
  finance: {
    title: 'Finance exceptions',
    description:
      'Payments and refunds whose outcome needs a person. Checking asks the provider again. Resolving records your decision and moves no money.',
    open: 'Open',
    resolved: 'Resolved',
    tabs: 'Show',
    empty: 'Nothing needs attention.',
    emptyResolved: 'No resolved exceptions yet.',
    loading: 'Loading exceptions',
    kind: {
      payment_unknown: 'Payment outcome unknown',
      refund_unknown: 'Refund outcome unknown',
      order_update_pending: 'Paid, but the order is not updated',
      amount_mismatch: 'Amount differs',
      reconcile_exhausted: 'The provider is not answering',
      refund_failed: 'Refund failed',
      provisioning_failed: 'Service not activated',
    },
    action: {
      check_status: 'Check the status',
      wait_for_provider: 'Wait for the provider',
      contact_provider: 'Contact the provider',
      review_order: 'Review the order',
      retry_refund: 'Check, then refund again if needed',
    },
    nextAction: 'Next safe step: {{action}}',
    providerState: 'Provider state: {{state}}',
    openedAt: 'Open since {{time}}',
    evidence: 'Evidence',
    order: 'Order {{reference}}',
    check: 'Check with the provider',
    resolve: 'Resolve',
    resolveTitle: 'Resolve this exception',
    resolution: 'Resolution',
    resolutions: {
      provider_confirmed: 'The provider confirmed the outcome',
      manual_review: 'Checked by hand',
      refunded_elsewhere: 'Refunded another way',
      written_off: 'Written off',
    },
    note: 'What you checked',
    resolveConfirm: 'Resolve exception',
    resolvedAs: 'Resolved: {{resolution}}',
  },
};
